import { ReactNode, useState } from "react";
import { CoreButton } from "../core/button/Button";
import styles from "./button.module.css";

export function DownloadLockdownModuleButton({
	size = "large",
	className,
	downloadUrl,
	getAuthCode,
	onError,
	loading,
	children,
}: {
	size?: "small" | "medium" | "large" | undefined;
	className?: string;
	downloadUrl?: string;
	getAuthCode?: () => Promise<{ authCode?: string } | undefined>;
	onError?: () => void;
	loading?: boolean;
	children: ReactNode;
}) {
	const [isFetchingBlob, setIsFetchingBlob] = useState(false);

	const handleForceDownload = async () => {
		if (!downloadUrl) {
			onError?.();
			return;
		}

		const urlObj = new URL(downloadUrl);
		const rawPath = urlObj.pathname;
		const originalName = decodeURIComponent(rawPath.substring(rawPath.lastIndexOf("/") + 1));
		const isExe = originalName.toLowerCase().endsWith(".exe");

		let authCode: string | undefined;

		if (getAuthCode && isExe) {
			try {
				const authCodeResult = await getAuthCode();
				authCode = authCodeResult?.authCode;
			} catch (e) {
				console.error("Failed to fetch access control auth code", e);
			}
		}

		if (!authCode || !isExe) {
			window.location.assign(downloadUrl);
			return;
		}

		try {
			setIsFetchingBlob(true);

			const dotIndex = originalName.lastIndexOf(".");
			const namePart = originalName.substring(0, dotIndex);
			const extPart = originalName.substring(dotIndex);
			const finalName = `${namePart}_${authCode}${extPart}`;

			const response = await fetch(downloadUrl);
			if (!response.ok) throw new Error("Failed to fetch file from CDN");

			const blob = await response.blob();
			const localUrl = window.URL.createObjectURL(blob);

			const tempLink = document.createElement("a");
			tempLink.href = localUrl;
			tempLink.download = finalName;
			document.body.appendChild(tempLink);

			tempLink.click();

			document.body.removeChild(tempLink);
			window.URL.revokeObjectURL(localUrl);
		} catch (error) {
			console.error("Blob download failed:", error);
			window.location.assign(downloadUrl);
		} finally {
			setIsFetchingBlob(false);
		}
	};

	return (
		<CoreButton
			size={size}
			className={`${className || styles.downloadButton}`}
			loading={loading || isFetchingBlob}
			loadingStyle="endSpinner"
			onClick={handleForceDownload}
		>
			{children}
		</CoreButton>
	);
}
